import React, { useEffect, useState } from 'react';
import { FaCircle, FaPause, FaPlay, FaRedoAlt } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import './CameraStreamPlayer.css';

const CAMERA_BUCKET = process.env.REACT_APP_CAMERA_BUCKET || 'camera-frames';
const LIVE_REFRESH_MS = 5000;
const HISTORY_REFRESH_MS = 30000;

function formatFrameTime(value) {
  if (!value) return 'horário indisponível';
  return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function CameraStreamPlayer({ camera, className = '' }) {
  const deviceId = String(camera?.device_id || '').trim().toLowerCase();
  const [liveFrame, setLiveFrame] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!deviceId || paused) return undefined;

    async function loadLiveFrame() {
      const storage = supabase.storage.from(CAMERA_BUCKET);
      const { data: files, error: listError } = await storage.list(deviceId, {
        limit: 5,
        search: 'live.jpg',
      });
      if (listError) throw listError;

      const file = files?.find(item => item.name === 'live.jpg');
      if (!file) throw new Error('A câmera ainda não enviou uma imagem.');

      const path = `${deviceId}/live.jpg`;
      const { data, error: urlError } = await storage.createSignedUrl(path, 60);
      if (urlError) throw urlError;
      if (!cancelled) {
        setLiveFrame({
          path,
          updatedAt: file.updated_at,
          url: `${data.signedUrl}&frame=${Date.now()}`,
        });
        setError('');
        setLoading(false);
      }
    }

    const refresh = () => loadLiveFrame().catch(fetchError => {
      if (!cancelled) {
        setError(fetchError.message || 'Não foi possível carregar a câmera.');
        setLoading(false);
      }
    });

    setLoading(true);
    refresh();
    const timer = window.setInterval(refresh, LIVE_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [deviceId, paused]);

  useEffect(() => {
    let cancelled = false;
    if (!deviceId) return undefined;

    async function loadHistory() {
      const storage = supabase.storage.from(CAMERA_BUCKET);
      const folder = `${deviceId}/history`;
      const { data: files, error: listError } = await storage.list(folder, {
        limit: 120,
        sortBy: { column: 'updated_at', order: 'desc' },
      });
      if (listError) throw listError;

      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      const recentFiles = (files || []).filter(file =>
        file.name.endsWith('.jpg') && new Date(file.updated_at).getTime() >= oneHourAgo
      );
      if (!recentFiles.length) {
        if (!cancelled) setHistory([]);
        return;
      }

      const paths = recentFiles.map(file => `${folder}/${file.name}`);
      const { data: signedFiles, error: urlError } = await storage.createSignedUrls(paths, 3600);
      if (urlError) throw urlError;
      if (!cancelled) {
        setHistory(signedFiles.map((signedFile, index) => ({
          name: recentFiles[index].name,
          path: paths[index],
          updatedAt: recentFiles[index].updated_at,
          url: signedFile.signedUrl,
        })).filter(frame => frame.url));
      }
    }

    const refresh = () => loadHistory().catch(historyError => {
      if (!cancelled) setError(historyError.message || 'Não foi possível carregar o histórico da câmera.');
    });
    refresh();
    const timer = window.setInterval(refresh, HISTORY_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [deviceId]);

  if (!deviceId) {
    return <div className={`camera-stream-empty ${className}`}>A câmera não possui um Device ID válido.</div>;
  }

  const displayedFrame = selectedFrame || liveFrame;
  const liveIsFresh = liveFrame?.updatedAt
    && Date.now() - new Date(liveFrame.updatedAt).getTime() <= 30000;
  const statusLabel = selectedFrame ? 'Histórico' : paused ? 'Pausado' : liveIsFresh ? 'Ao vivo' : 'Sem sinal';

  return (
    <div className={`camera-stream-player ${className}`}>
      <div className="camera-stream-toolbar">
        <span className={`camera-stream-status${paused || selectedFrame || !liveIsFresh ? ' is-paused' : ''}`}>
          <FaCircle /> {statusLabel}
        </span>
        <span className="camera-stream-time">{formatFrameTime(displayedFrame?.updatedAt)}</span>
        <button type="button" onClick={() => setPaused(value => !value)} title={paused ? 'Retomar atualização' : 'Pausar atualização'}>
          {paused ? <FaPlay /> : <FaPause />}
        </button>
        {selectedFrame && (
          <button type="button" onClick={() => { setSelectedFrame(null); setPaused(false); }} title="Voltar ao vivo">
            <FaRedoAlt />
          </button>
        )}
      </div>

      <div className="camera-stream-stage">
        {displayedFrame?.url && (
          <img
            src={displayedFrame.url}
            alt={`Monitoramento de ${camera?.nome_sensor || camera?.device_id || 'câmera'}`}
            onError={() => setError('A imagem da câmera não pôde ser exibida.')}
          />
        )}
        {loading && !displayedFrame && <div className="camera-stream-loading">Buscando imagem da câmera…</div>}
        {!loading && !displayedFrame && <div className="camera-stream-empty">Nenhuma imagem recebida.</div>}
      </div>

      {error && <div className="camera-stream-error" role="alert">{error}</div>}

      {history.length > 0 && (
        <section className="camera-history" aria-label="Imagens da última hora">
          <div className="camera-history-heading">
            <strong>Última hora</strong>
            <span>{history.length} imagens</span>
          </div>
          <div className="camera-history-strip">
            {history.map(frame => (
              <button
                type="button"
                className={selectedFrame?.path === frame.path ? 'is-selected' : ''}
                key={frame.path}
                onClick={() => { setSelectedFrame(frame); setPaused(true); }}
                title={`Ver imagem das ${formatFrameTime(frame.updatedAt)}`}
              >
                <img src={frame.url} alt="" loading="lazy" />
                <span>{formatFrameTime(frame.updatedAt)}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
